// eslint-disable-next-line @nx/enforce-module-boundaries
import type {
  IMetaData,
  IMerchandisingAEMImage,
  IMerchandisingDCAImage,
  ITargetContent,
} from '@marriott/mi-merch-components';
import type { renditions, TrackingPropsInterface } from '@marriott/mi-ui-library';
import { SlotContent } from '../../organisms/DynamicTabbedLayout/DynamicTabbedLayout.types';
import type { SlotTrackingContext } from '../slotPropNormalizerUtils';

export type RenditionList = renditions['renditions'];
export type LayeredFeatureImage = IMerchandisingAEMImage;
export type LayeredFeatureDCAImage = IMerchandisingDCAImage;
export type LayeredFeatureContentPayload = DcaContentPayload | LayeredFeatureCfContent;
export type DcaMetadata = IMetaData;
export type LayeredFeatureAdapterCtx = LayeredFeatureCfPayload | LayeredFeatureDCAPayload;

export interface LayeredFeatureTrackingProperties {
  [key: string]: string;
}

export type DcaImageUrls = {
  panoramicHorizontal?: string;
  panoramicVertical?: string;
  wideHorizontal?: string;
  wideVertical?: string;
  featureHorizontal?: string;
  featureVertical?: string;
  classicHorizontal?: string;
  classicVertical?: string;
  standardVertical?: string;
  standardHorizontal?: string;
  square?: string;
  deviceSmall?: string;
  flex?: string;
};

export interface DcaImage {
  imageUrls?: DcaImageUrls;
  imageSrc?: string;
  alternateDescription?: string;
  assetPath?: string;
  defaultImage?: boolean;
  roomTypeCodes?: string;
  title?: string;
  caption?: string;
  marketingCaption?: string;
}

export interface DcaContentPayload {
  eyebrowText?: string;
  headerLong?: string;
  headerMedium?: string;
  headerShort?: string;
  descriptionLong?: string;
  descriptionMedium?: string;
  descriptionShort?: string;
  callToActionText1?: string;
  callToActionText2?: string;
  destinationUrl1?: string;
  destinationUrl2?: string;
  image?: DcaImage;
  imageAltText?: string;
  openInNewTab1?: boolean;
  openInNewTab2?: boolean;
  primaryCtaAriaLabel?: string;
  secondaryCtaAriaLabel?: string;
  mathLabel1?: string | null;
  mathLabel2?: string | null;
  mathLabel3?: string | null;
  mathValue?: string | null;
  mathType?: string | null;
  mathTitle?: string | null;
  legalLong?: string;
  legalShort?: string;
  caption?: string;
  captionUrl?: string;
  imageAssetId?: string;
  logoAssetId?: string;
  logo?: DcaImage;
  mboxName?: string;
  isTargetContentLoaded?: boolean;
  metadata?: unknown;
  promotionalTag?: string;
  photoCaption?: string;
  logoImageAltText?: string;
}

export interface LayeredFeatureCfContent extends IMerchandisingAEMImage {
  icon?: string;
  logoImageAltText?: string;
  eyebrow?: string;
  header?: string;
  title?: string;
  description?: string | { plaintext?: string };
  assetPath?: string;
  altText?: string;
  desktopVariation?: string;
  tabletVariation?: string;
  mobileVariation?: string;
  primaryCTALinkText?: string;
  primaryCtaLinkText?: string;
  primaryCTALink?: string;
  primaryCtaLink?: string;
  primaryCtaSize?: string;
  primaryCtaOpenInNewTab?: boolean;
  primaryCtaAriaLabel?: string;
  secondaryCtaLinkText?: string;
  secondaryCtaLink?: string;
  secondaryCtaSize?: string;
  secondaryCtaOpenInNewTab?: boolean;
  secondaryCtaAriaLabel?: string;
  tertiaryCtaLinkText?: string;
  tertiaryCtaLink?: string;
  tertiaryOpenInANewTab?: boolean;
  tertiaryLinkAriaLabel?: string;
  styleclass?: string;
  styleClass?: string;
  trackingProperties?: LayeredFeatureTrackingProperties;
  componentId?: string;
  custom_click_track_value_primary?: string;
  theme?: string;
  photoCaption?: string;
}

export interface LayeredFeatureDcaProps extends Partial<ITargetContent> {
  componentId?: string;
  eyebrowText?: string;
  headerLong?: string;
  headerMedium?: string;
  headerShort?: string;
  descriptionLong?: string;
  descriptionMedium?: string;
  descriptionShort?: string;
  callToActionText1?: string;
  destinationUrl1?: string;
  openInNewTab1?: boolean;
  callToActionSize1?: string;
  callToActionText2?: string;
  destinationUrl2?: string;
  openInNewTab2?: boolean;
  callToActionSize2?: string;
  primaryCtaAriaLabel?: string;
  secondaryCtaAriaLabel?: string;
  tertiaryLinkText?: string;
  tertiaryLinkUrl?: string;
  tertiaryOpenInANewTab?: boolean;
  tertiaryLinkAriaLabel?: string;
  backgroundImage?: IMerchandisingDCAImage;
  className?: string;
  trackingProperties?: TrackingPropsInterface;
  custom_click_track_value_primary?: string;
  logo?: IMerchandisingDCAImage;
  legalLong?: string;
  mboxParameter?: string;
  caption?: string;
  theme?: string;
  type?: 'cf' | 'dca';
  desktopVariation?: string;
  tabletVariation?: string;
  mobileVariation?: string;
  logoImageAltText?: string;
  /** DCA payloads use `metadata`; ITargetContent uses `metaData` */
  metadata?: IMetaData;
}

export type LayeredFeatureDCAPayload = {
  slot: SlotContent;
  contentObj: DcaContentPayload;
  baseUrl: string;
  alt: string;
  trackingCtx?: SlotTrackingContext;
};

export type LayeredFeatureCfPayload = {
  slot: SlotContent;
  contentObj: LayeredFeatureCfContent;
  baseUrl: string;
  alt: string;
  trackingCtx?: SlotTrackingContext;
};

export function isCfContent(
  ctx: LayeredFeatureAdapterCtx
): ctx is LayeredFeatureAdapterCtx & { contentObj: LayeredFeatureCfContent } {
  return ctx.slot?.type === 'cf';
}

export function isDcaContent(
  ctx: LayeredFeatureAdapterCtx
): ctx is LayeredFeatureAdapterCtx & { contentObj: DcaContentPayload } {
  return ctx.slot?.type === 'dca';
}

export function isCoBrandDca(ctx: LayeredFeatureAdapterCtx): boolean {
  if (!isDcaContent(ctx)) return false;
  // WEB-191033: Target payloads have been observed using both `metadata` and
  // `metaData` spellings. Accept either so routing and buildLayeredFeatureTracking
  // (which already reads `metaData ?? metadata`) stay in sync — otherwise a
  // PascalCase payload would route to the base adapter while the inner tracking
  // builder still detected cobrand, producing mixed props.
  const contentObj = ctx.contentObj as Record<string, unknown>;
  const metadata = ((contentObj['metaData'] ?? contentObj['metadata']) as IMetaData | undefined) ?? undefined;
  return metadata?.type?.toLowerCase() === 'cobrand';
}
